import {
  createNodeId,
  type BoardNode,
  type ConnectorEndpoint,
  type NodeId,
  type Vec,
} from '@kanvas/board-core';

export interface CloneResult {
  nodes: BoardNode[];
  /** Old id to new id, so callers can select the copies. */
  idMap: Map<NodeId, NodeId>;
}

/**
 * Deep-copies a set of nodes with fresh ids, rewriting every internal
 * reference.
 *
 * Getting this right is what makes copy/paste and duplicate feel correct: a
 * connector between two copied shapes must attach to the *copies*, while a
 * connector to a shape that was not copied keeps pointing at the original.
 * Group membership and frame parenting follow the same rule.
 */
export function cloneNodes(
  nodes: readonly BoardNode[],
  options: {
    offset?: Vec;
    indices?: readonly string[];
    userId: string;
    /** Parent to assign when the original parent is outside the copied set. */
    fallbackParentId?: NodeId | null;
  },
): CloneResult {
  const offset = options.offset ?? { x: 0, y: 0 };
  const idMap = new Map<NodeId, NodeId>();
  for (const node of nodes) idMap.set(node.id, createNodeId());

  const now = Date.now();
  const remapEndpoint = (ep: ConnectorEndpoint): ConnectorEndpoint => {
    if (ep.kind === 'point') return { kind: 'point', x: ep.x + offset.x, y: ep.y + offset.y };
    const mapped = idMap.get(ep.nodeId);
    // Endpoint outside the copied set: keep the original target, unshifted.
    return mapped ? { ...ep, nodeId: mapped } : ep;
  };

  const out: BoardNode[] = nodes.map((node, i) => {
    const id = idMap.get(node.id)!;
    const parentId =
      node.parentId && idMap.has(node.parentId)
        ? idMap.get(node.parentId)!
        : (options.fallbackParentId ?? null);

    const base = {
      ...node,
      id,
      parentId,
      x: node.x + offset.x,
      y: node.y + offset.y,
      index: options.indices?.[i] ?? node.index,
      createdBy: options.userId,
      createdAt: now,
      updatedAt: now,
    } as BoardNode;

    if (base.type === 'connector') {
      return {
        ...base,
        start: remapEndpoint(base.start),
        end: remapEndpoint(base.end),
        bend: base.bend.map((b) => ({ x: b.x + offset.x, y: b.y + offset.y })),
      };
    }
    if (base.type === 'group') {
      // Children outside the copied set are dropped: a group cannot own a node
      // it does not have a copy of.
      return {
        ...base,
        children: base.children.flatMap((c) => {
          const mapped = idMap.get(c);
          return mapped ? [mapped] : [];
        }),
      };
    }
    if (base.type === 'comment') {
      return {
        ...base,
        anchorNodeId: base.anchorNodeId ? (idMap.get(base.anchorNodeId) ?? null) : null,
      };
    }
    return base;
  });

  return { nodes: out, idMap };
}

/**
 * Expands a selection to everything that must travel with it: group members,
 * and connectors whose *both* ends are inside the set.
 */
export function expandForClone(
  ids: readonly NodeId[],
  all: readonly BoardNode[],
  lookup: (id: NodeId) => BoardNode | undefined,
): BoardNode[] {
  const chosen = new Set<NodeId>(ids);

  // Pull in group children, transitively.
  const queue = [...ids];
  while (queue.length > 0) {
    const id = queue.pop()!;
    const node = lookup(id);
    if (node?.type === 'group') {
      for (const child of node.children) {
        if (!chosen.has(child)) {
          chosen.add(child);
          queue.push(child);
        }
      }
    }
  }

  for (const node of all) {
    if (node.type !== 'connector' || chosen.has(node.id)) continue;
    const a = node.start.kind === 'node' && chosen.has(node.start.nodeId);
    const b = node.end.kind === 'node' && chosen.has(node.end.nodeId);
    if (a && b) chosen.add(node.id);
  }

  // Preserve paint order so the copies keep their relative stacking.
  return all.filter((n) => chosen.has(n.id));
}
